import { HttpClient } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'

import { Observable } from 'rxjs'

import { AuditUser } from '@flowx/designer/modules/core/dictionary/versioning.dictionary'
import { ApiHttpService } from '@flowx/designer/shared/api/api-http.service'

import { PolicyType } from './active-policy.api'

export enum TargetType {
  USERNAME = 'USERNAME',
  ROLE = 'ROLE',
  GROUP = 'GROUP',
}

export interface PolicyOverride {
  id: string
  workspaceId: string
  applicationId: string
  targetType: TargetType
  targetValue: string
  targetLabel?: string
  policyType: PolicyType
  policyBranchId?: string
  policyBuildId?: string
  priority?: number
  enabled: boolean
  createdBy: AuditUser
  createdDate: Date
  modifiedBy: AuditUser
  modifiedDate: Date
}

export interface CreatePolicyOverrideDTO {
  targetType: TargetType
  targetValue: string
  policyType: PolicyType
  policyBranchId?: string
  policyBuildId?: string
  priority?: number
  enabled: boolean
}

export interface UpdatePolicyOverrideDTO {
  policyType: PolicyType
  policyBranchId?: string
  policyBuildId?: string
  priority?: number
  enabled: boolean
}

export interface EffectivePolicyResult {
  policyType: PolicyType
  policyBranchId?: string
  policyBuildId?: string
  branchLabel?: string
  buildLabel?: string
  resolutionPath: {
    description: string
    matched: boolean
  }[]
}

@Injectable({ providedIn: 'root' })
export class ActivePolicyOverrideApiService extends ApiHttpService {
  constructor() {
    const baseUrl = inject<string>('BASE_URL' as any)
    const httpClient = inject(HttpClient)
    super(baseUrl, httpClient)
  }

  getOverrides(applicationId: string): Observable<PolicyOverride[]> {
    return this.get(`${this.buildOverridesUrl(applicationId)}`)
  }

  createOverride(
    applicationId: string,
    override: CreatePolicyOverrideDTO
  ): Observable<PolicyOverride> {
    return this.post(`${this.buildOverridesUrl(applicationId)}`, override)
  }

  updateOverride(
    applicationId: string,
    overrideId: string,
    update: UpdatePolicyOverrideDTO
  ): Observable<PolicyOverride> {
    return this.put(`${this.buildOverridesUrl(applicationId)}/${overrideId}`, update)
  }

  deleteOverride(applicationId: string, overrideId: string): Observable<void> {
    return this.delete(`${this.buildOverridesUrl(applicationId)}/${overrideId}`)
  }

  deleteAllRoleOverrides(applicationId: string): Observable<void> {
    return this.delete(`${this.buildOverridesUrl(applicationId)}/role`)
  }

  testEffectivePolicy(
    applicationId: string,
    username: string,
    roles: string[]
  ): Observable<EffectivePolicyResult> {
    return this.post(`${this.buildPolicyUrl(applicationId)}/test-effective`, { username, roles })
  }

  private buildOverridesUrl = (applicationId: string): string =>
    `rtm/api/build-mgmt/app/${applicationId}/active-build-policy/overrides`

  private buildPolicyUrl = (applicationId: string): string =>
    `rtm/api/build-mgmt/app/${applicationId}/active-build-policy`
}
